import { isoToLocalShortDate } from '@utils'
import { logout } from '@utils/sessions'
import { LoadingButton } from '@mui/lab'
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    colors,
} from '@mui/material'
import { useEffect, useState } from 'react'
import DashboardErrorView from '../components/DashboardErrorView'
import DashboardLayout from '../components/DashboardLayout'
import DashboardLoadingDataView from '../components/DashboardLoadingDataView'
import SearchIcon from '@mui/icons-material/Search'
import TableCellSubText from '@components/TableCellSubText'
import TableHeadCell from '@components/TableHeadCell'
import usePagination from '@hooks/usePagination'
import API from '@enums/api'

export type UserSearchRow = {
    id: string
    name: string
    first_name: string | null
    last_name: string | null
    email: string
    created_at: string
    deleted_at: string
}

const UserSearchPage = () => {
    const [view, setView] = useState<'search' | 'results'>('search')
    const [query, setQuery] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [rows, setRows] = useState<UserSearchRow[]>([])
    const {
        handlePageChange,
        handleRowsPerPageChange,
        page,
        paginate,
        rowsPerPage,
    } = usePagination()

    /**
     * Page initialisation
     */
    useEffect(() => {
        setLoading(true)
        // Set search values based on URL query parameters if available
        // Useful for when searches are sent between colleagues for example
        const searchParams = new URLSearchParams(window.location.search)
        const q = searchParams.get('q')
        setQuery(q ?? '')
        setLoading(false)
    }, [])

    const handleSearch: React.FormEventHandler<HTMLFormElement> = async (
        event
    ) => {
        event.preventDefault()

        if (!query) {
            return
        }

        // update the search params in the URL
        const searchParams = new URLSearchParams()
        searchParams.set('q', query)
        history.pushState(
            null,
            '',
            window.location.pathname + '?' + searchParams.toString()
        )

        // search for results
        setLoading(true)
        const res = await API.searches.users(query)
        setLoading(false)

        // switch to results view
        setView('results')

        if (!res.ok) {
            if (res.status === 401) {
                logout()
                return
            }
            console.error(res)
            setError('Failed to search')
        } else {
            setRows(res.value)
        }
    }

    if (view === 'search') {
        return (
            <DashboardLayout>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: 64,
                    }}
                >
                    <Paper
                        style={{
                            paddingInline: 32,
                            paddingBlock: 32,
                            textAlign: 'center',
                        }}
                    >
                        <p style={{ marginBottom: 16 }}>
                            Search by a user’s name, email,
                            <br />
                            or Habitat Account ID
                        </p>

                        <form onSubmit={handleSearch}>
                            <TextField
                                onChange={(e) => setQuery(e.target.value)}
                                value={query}
                                label="Search"
                                placeholder="John Doe"
                                sx={{ width: '100%' }}
                            />
                            <LoadingButton
                                sx={{ marginTop: '32px' }}
                                startIcon={<SearchIcon />}
                                size="small"
                                variant="contained"
                                type="submit"
                                loading={loading}
                            >
                                Search
                            </LoadingButton>
                        </form>
                    </Paper>
                </div>
            </DashboardLayout>
        )
    }

    if (loading) {
        return <DashboardLoadingDataView loading={loading} />
    }

    if (error) {
        return <DashboardErrorView error={error} loading={loading} />
    }

    const rowCount = rows.length
    const filteredRows = paginate(rows, page, rowsPerPage)

    return (
        <DashboardLayout loading={loading}>
            <div
                style={{
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                }}
            >
                <div>
                    <form onSubmit={handleSearch}>
                        <TextField
                            label="Search"
                            variant="standard"
                            onChange={(e) => setQuery(e.target.value)}
                            value={query}
                        />
                    </form>
                </div>
                <div>
                    <p
                        style={{
                            color: 'var(--grey-700)',
                            fontSize: '0.875rem',
                        }}
                    >
                        <strong>{rowCount} results</strong>
                    </p>
                </div>
            </div>
            <TableContainer
                component={Paper}
                sx={{
                    maxWidth: 'calc(100vw - 240px - 32px)',
                    width: '100%',
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableHeadCell>
                                Name
                                <br />
                                <TableCellSubText>
                                    Habitat Account ID
                                </TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell>Email</TableHeadCell>
                            <TableHeadCell>
                                Created
                                <br />
                                <TableCellSubText>Deleted</TableCellSubText>
                            </TableHeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    No results found
                                    <br />
                                    <TableCellSubText>
                                        Try changing your search or filters
                                    </TableCellSubText>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredRows.map((row) => {
                                return (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        style={{
                                            backgroundColor: row.deleted_at
                                                ? colors.red[50]
                                                : undefined,
                                        }}
                                    >
                                        <TableCell>
                                            {row.name}
                                            <br />
                                            <TableCellSubText>
                                                {row.id}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>
                                            {isoToLocalShortDate(
                                                row.created_at
                                            )}
                                            {row.deleted_at && (
                                                <>
                                                    <br />
                                                    <TableCellSubText>
                                                        {isoToLocalShortDate(
                                                            row.deleted_at
                                                        )}
                                                    </TableCellSubText>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={rowCount}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                page={page}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50, { value: -1, label: 'All' }]}
            />
        </DashboardLayout>
    )
}

export default UserSearchPage
